import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventsApi from '../../../api/eventsApi/eventsApi';
import { PinQuestion, PinQuestionDetailsAnswer } from '../../../api/eventsApi/eventsApi';

interface EvaluationModalProps {
    open: boolean;
    onClose: () => void;
    pinId: string;
    userId: string;
}

const EvaluationModal: React.FC<EvaluationModalProps> = ({ open, onClose, pinId, userId }) => {
    const [evaluationText, setEvaluationText] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [evaluationQuestion, setEvaluationQuestion] = useState<PinQuestion | null>(null);

    useEffect(() => {
        const fetchExistingEvaluation = async () => {
            if (!open || !pinId) return;

            try {
                setIsLoading(true);
                
                const response = await EventsApi.getPinQuestionsAndAnswers(pinId);
                
                // Find the evaluation question
                const evalQuestion = response.questions.find(q => q.text === "Sonuç ve Değerlendirme");
                
                if (evalQuestion) {
                    setEvaluationQuestion(evalQuestion);
                    // If there's an answer, set its description
                    if (evalQuestion.answers.length > 0) {
                        setEvaluationText(evalQuestion.answers[0].description || '');
                    } else {
                        setEvaluationText('');
                    }
                }
            } catch (error) {
                console.error('Error fetching evaluation:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchExistingEvaluation();
    }, [open, pinId]);

    const handleSave = async () => {
        if (!pinId || !userId || !evaluationQuestion) {
            console.error('Pin ID, User ID or Evaluation Question is missing');
            return;
        }

        try {
            setIsSaving(true);
            
            const existingAnswer = evaluationQuestion.answers[0];

            if (existingAnswer) {
                // If answer exists, update it
                const formData = new FormData();
                formData.append('Id', existingAnswer.id);
                formData.append('QuestionId', evaluationQuestion.id);
                formData.append('UserId', userId);
                formData.append('YesNo', 'true');
                formData.append('Findings', '');
                formData.append('PinId', pinId);
                formData.append('ProposedSolutions', '');
                formData.append('Description', evaluationText);
                
                await EventsApi.updateQuestionAnswer(formData);
            } else {
                // If no answer exists, create new one
                await EventsApi.saveQuestionAnswers({
                    questionId: evaluationQuestion.id,
                    userId: userId,
                    yesNo: true,
                    findings: '',
                    pinId: pinId,
                    proposedSolutions: '',
                    description: evaluationText
                });
            }

            onClose();
        } catch (error) {
            console.error('Error saving evaluation:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ m: 0, p: 2, paddingRight: '48px' }}>
                Sonuç ve Değerlendirme
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    multiline
                    rows={10}
                    value={evaluationText}
                    onChange={(e) => setEvaluationText(e.target.value)}
                    placeholder="Değerlendirme metnini buraya giriniz..."
                    sx={{ mt: 2 }}
                    disabled={isLoading}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={isSaving}
                    variant="contained"
                    sx={{
                        backgroundColor: '#fff',
                        color: '#094151',
                        border: '1.5px solid #094151',
                        fontWeight: 600,
                        borderRadius: '8px',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#d0e7ec',
                            color: '#094151',
                        }
                    }}
                >
                    İptal
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={isSaving || !evaluationText.trim()}
                    sx={{
                        backgroundColor: '#094151',
                        color: '#fff',
                        fontWeight: 600,
                        borderRadius: '8px',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#022631',
                            color: '#fff',
                        }
                    }}
                >
                    {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EvaluationModal; 